/**
 * CUSTOM asset for sections/pdp-buybox.liquid.
 * UPSTREAM risk: LOW (net-new file, no Horizon internals imported/modified).
 *
 * Handles the two pieces of PDP buy-box behaviour that have no native
 * Horizon equivalent:
 *   1. <hl-plan-selector> — subscribe vs one-time picker. Sets a hidden
 *      `selling_plan` input (associated with the real Horizon product form
 *      via the HTML `form` attribute) and toggles active styling. Prices
 *      are pre-rendered server-side per plan, so this only ever toggles
 *      visibility/attributes — it never computes or formats money client-side.
 *   2. <hl-gallery> — thumbnail click swaps the main gallery image. This is
 *      a cosmetic image browser only; it is intentionally NOT wired to
 *      variant selection (Horizon's own `_product-media-gallery` owns that
 *      job — see the "known limitation" note in pdp-buybox.liquid).
 *
 *   3. <hl-sticky-cta> — the floating buy prompt. Replaced Horizon's
 *      <sticky-add-to-cart> on 2026-08-05; it scrolls to the buy box rather
 *      than adding to cart, so none of that component's machinery applied.
 *
 * <hl-plan-selector> dispatches `hl:plan-changed`. That event has no listener
 * as of 2026-08-05 — the old sticky bar consumed it to show a plan cadence
 * label, and the rebuilt CTA carries no plan copy. Kept as the seam for
 * anything that later needs to react to plan selection.
 */

class HlPlanSelector extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-plan-option]'));
    this.input = this.querySelector('[data-plan-input]');
    if (!this.cards.length || !this.input) return;

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.select(card));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.select(card);
        }
      });
    });

    // Initial state: whichever card has data-plan-default="true" (subscribe,
    // per the architecture doc's subscribe-first default), else the first.
    const initial = this.cards.find((card) => card.dataset.planDefault === 'true') || this.cards[0];
    this.select(initial, { silent: true });
  }

  select(card, { silent = false } = {}) {
    this.cards.forEach((c) => {
      const isSelected = c === card;
      c.classList.toggle('hl-plan-card--selected', isSelected);
      c.setAttribute('aria-checked', String(isSelected));
    });
    this.input.value = card.dataset.sellingPlanId || '';

    if (!silent) {
      this.dispatchEvent(
        new CustomEvent('hl:plan-changed', {
          bubbles: true,
          detail: {
            label: card.dataset.planLabel || '',
            price: card.dataset.planPrice || '',
          },
        })
      );
    } else {
      // Still tell the sticky bar what the initial default is, once it exists.
      queueMicrotask(() => {
        this.dispatchEvent(
          new CustomEvent('hl:plan-changed', {
            bubbles: true,
            detail: {
              label: card.dataset.planLabel || '',
              price: card.dataset.planPrice || '',
            },
          })
        );
      });
    }
  }
}

class HlGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('.hl-gallery__stage');
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    if (!this.track || !this.slides.length) return;

    this.activeIndex = 0;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    }

    // Track which slide is actually in view (native swipe/scroll on mobile
    // moves the track without going through goTo()), and keep the stage's
    // aspect-ratio + active thumbnail in sync with it either way.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.find((entry) => entry.isIntersecting);
          if (!visible) return;
          const index = this.slides.indexOf(visible.target);
          if (index !== -1) this.setActive(index);
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    this.setActive(0);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.slides[clamped].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  setActive(index) {
    this.activeIndex = index;
    const ratio = this.slides[index]?.dataset.aspectRatio;
    if (this.stage && ratio) {
      this.stage.style.setProperty('--hl-gallery-ratio', ratio);
    }
    this.thumbs.forEach((t) => t.classList.toggle('hl-gallery__thumb--active', Number(t.dataset.index) === index));
    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
  }
}

/**
 * <hl-sticky-cta> — the floating buy prompt.
 *
 * Replaced Horizon's <sticky-add-to-cart> on 2026-08-05. That component exists
 * to add to cart (quantity sync, fly-to-cart, re-morph on variant change) and
 * enforces requiredRefs of addToCartButton/quantityDisplay/quantityNumber.
 * This button scrolls to the buy box instead, so none of that applied — see
 * the rationale comment in sections/pdp-buybox.liquid.
 *
 * Visibility is driven by an IntersectionObserver on the buy box rather than a
 * scroll listener: the bar's only job is to appear once the real buy controls
 * are off screen, which is exactly what the observer reports, and it costs no
 * work on scroll frames.
 */
class HlStickyCta extends HTMLElement {
  connectedCallback() {
    this.target = document.querySelector(this.dataset.target || '');
    this.button = this.querySelector('[data-sticky-cta]');
    if (!this.target || !this.button) return;

    this.button.addEventListener('click', () => this.jumpToBuyBox());

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = entry.isIntersecting ? 'false' : 'true';
      },
      // A sliver of the buy box still counts as "in view" — flipping the bar on
      // the moment the last pixel leaves would make it flicker while scrubbing.
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );
    this.observer.observe(this.target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * Scroll the buy box back into view and move focus to the first control the
   * customer needs — the variant picker or plan selector when one exists, the
   * add-to-cart button otherwise. Without the focus move this would be a
   * purely visual jump, leaving keyboard and screen-reader users where they
   * were; tabindex="-1" is set only as a fallback so a non-focusable container
   * can still receive it, and is removed again afterwards.
   */
  jumpToBuyBox() {
    // Order matters: land on the FIRST decision the customer still has to
    // make, not the last. Quantity/pack tier, then purchase option (subscribe
    // vs one-time), then variant, and only then the add-to-cart button when
    // there's nothing left to choose.
    const focusTarget =
      this.target.querySelector(
        '[data-qty-card], [data-plan-card], [data-plan-option], variant-picker input:not([disabled]), select, [ref="addToCartButton"], button[type="submit"]'
      ) || this.target;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    const hadTabindex = focusTarget.hasAttribute('tabindex');
    if (!hadTabindex) focusTarget.setAttribute('tabindex', '-1');
    // preventScroll so focusing doesn't fight the smooth scroll above.
    focusTarget.focus({ preventScroll: true });
    if (!hadTabindex) {
      focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
    }
  }
}

if (!customElements.get('hl-plan-selector')) {
  customElements.define('hl-plan-selector', HlPlanSelector);
}
if (!customElements.get('hl-gallery')) {
  customElements.define('hl-gallery', HlGallery);
}
if (!customElements.get('hl-sticky-cta')) {
  customElements.define('hl-sticky-cta', HlStickyCta);
}
